import { Checkbox } from '@carbon/react';
import { useAtom } from 'jotai';
import style from 'components/map/map.module.scss';
import {
  horizontalUnitsLayerAtom,
  horizontalWellsLayerAtom,
  subtractLayerAtom,
  unitAllocationTractsLayerAtom,
  verticalUnitsLayerAtom,
  verticalWellsLayerAtom,
} from './atoms';

/**
 * Scoped layer selection for the CRM Tract wizard map sidebar.
 * Rendered via the `scopedLayers` prop on `<Map>` — appears under the
 * "Scoped" heading in the `StylesSelector` panel, above the "General"
 * section. Matches the packages page `ScopedPackageLayerSelection`
 * pattern using the same `map.module.scss` classes.
 *
 * @return {JSX.Element} The scoped layer checkboxes.
 */
export const ScopedCrmLayerSelection = (): JSX.Element => {
  const [horizontalUnits, setHorizontalUnits] = useAtom(
    horizontalUnitsLayerAtom
  );
  const [verticalUnits, setVerticalUnits] = useAtom(verticalUnitsLayerAtom);
  const [horizontalWells, setHorizontalWells] = useAtom(
    horizontalWellsLayerAtom
  );
  const [verticalWells, setVerticalWells] = useAtom(verticalWellsLayerAtom);
  const [uats, setUats] = useAtom(unitAllocationTractsLayerAtom);
  const [subtract, setSubtract] = useAtom(subtractLayerAtom);

  // Combined state for master checkbox
  const unitsChecked = horizontalUnits || verticalUnits;
  const wellsChecked = horizontalWells || verticalWells;
  const allStates = [unitsChecked, subtract, wellsChecked, uats];
  const allChecked = allStates.every(Boolean);
  const indeterminate = allStates.some(Boolean) && !allChecked;

  /**
   * Handles the master "Layers" checkbox toggle.
   * @param {React.ChangeEvent<HTMLInputElement>} _evt - Change event.
   * @param {{ checked: boolean }} param1 - New checked state.
   */
  const handleAllChange = (
    _evt: React.ChangeEvent<HTMLInputElement>,
    { checked }: { checked: boolean }
  ) => {
    setHorizontalUnits(checked);
    setVerticalUnits(checked);
    setSubtract(checked);
    setHorizontalWells(checked);
    setVerticalWells(checked);
    setUats(checked);
  };

  return (
    <>
      <div className={style.titleArea}>
        <div className={style.allCheckBox}>
          <Checkbox
            id="crm-scoped-layers-all"
            labelText="Layers"
            checked={allChecked}
            indeterminate={indeterminate}
            onChange={handleAllChange}
          />
        </div>
      </div>
      <div className={style.groupChecks}>
        <div className={style.scopedCrmUnits}>
          <Checkbox
            id="crm-scoped-units"
            labelText="Units"
            checked={unitsChecked}
            onChange={(
              _evt: React.ChangeEvent<HTMLInputElement>,
              { checked }: { checked: boolean }
            ) => {
              setHorizontalUnits(checked);
              setVerticalUnits(checked);
            }}
          />
        </div>
        <div className={style.scopedCrmSubtract}>
          <Checkbox
            id="crm-scoped-subtract"
            labelText="Subtract"
            checked={subtract}
            onChange={(
              _evt: React.ChangeEvent<HTMLInputElement>,
              { checked }: { checked: boolean }
            ) => setSubtract(checked)}
          />
        </div>
        <div className={style.scopedCrmWells}>
          <Checkbox
            id="crm-scoped-wells"
            labelText="Wells"
            checked={wellsChecked}
            onChange={(
              _evt: React.ChangeEvent<HTMLInputElement>,
              { checked }: { checked: boolean }
            ) => {
              setHorizontalWells(checked);
              setVerticalWells(checked);
            }}
          />
        </div>
        <div className={style.scopedCrmUats}>
          <Checkbox
            id="crm-scoped-uats"
            labelText="Unit Allocation Tracts"
            checked={uats}
            onChange={(
              _evt: React.ChangeEvent<HTMLInputElement>,
              { checked }: { checked: boolean }
            ) => setUats(checked)}
          />
        </div>
      </div>
    </>
  );
};
